import { lazy, Suspense, useEffect, useState } from "react";

const ReactECharts = lazy(() => import("echarts-for-react"));

// Rich, high-contrast categorical palette (works in light and dark).
export const SCI_PALETTE = [
  "#6366f1", // indigo
  "#ec4899", // pink
  "#f97316", // orange
  "#22c55e", // green
  "#06b6d4", // cyan
  "#eab308", // amber
  "#8b5cf6", // violet
  "#ef4444", // red
  "#14b8a6", // teal
  "#f43f5e", // rose
  "#3b82f6", // blue
  "#84cc16", // lime
];

// Sequential gradient stops for heatmaps.
export const SCI_HEAT = [
  "#0d1b4c",
  "#3b3fb6",
  "#6366f1",
  "#ec4899",
  "#f97316",
  "#fde047",
];

/** Bumps whenever the <html> theme class changes so charts re-read tokens. */
export function useThemeVersion(): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const observer = new MutationObserver(() => setVersion((v) => v + 1));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);
  return version;
}

export interface SciChartTheme {
  text: string;
  muted: string;
  border: string;
}

export function getSciChartTheme(): SciChartTheme {
  const style =
    typeof document !== "undefined"
      ? getComputedStyle(document.documentElement)
      : null;
  const read = (prop: string, fallback: string) =>
    style?.getPropertyValue(prop).trim() || fallback;
  return {
    text: read("--text-base", "oklch(0.95 0.01 250)"),
    muted: read("--text-secondary", "oklch(0.65 0.02 250)"),
    border: read("--border-c", "oklch(0.3 0.02 250)"),
  };
}

/** Lazy echarts-for-react wrapper with a transparent background + fixed height. */
export function EChartsPlot({
  option,
  height = 380,
  testId,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: ECharts option objects are deeply typed; the callers build valid ones.
  option: any;
  height?: number;
  testId?: string;
}) {
  return (
    <div data-testid={testId} style={{ height }}>
      <Suspense
        fallback={
          <div className="h-full w-full animate-pulse rounded-xl bg-[var(--surface-subtle)]" />
        }
      >
        <ReactECharts
          option={{ backgroundColor: "transparent", ...option }}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer: "canvas" }}
          notMerge
        />
      </Suspense>
    </div>
  );
}
